'use client';

import { useState } from 'react';
import { faArrowLeft } from '@fortawesome/pro-light-svg-icons';
import { DataTestIdEnum } from '@medprev-web-app/datatestid';

import { PatientSelectionModalKeys } from '@/app/(main)/_components/patient-selection/_types/patient-selection-modal-keys.enum';
import { Button } from '@/components/ui/button/button.component';
import { Icon } from '@/components/ui/icon.component';
import { Input } from '@/components/ui/input/input.component';
import { Typography } from '@/components/ui/typography/typography.component';
import { TypographyVariant } from '@/components/ui/typography/typography.type';
import { useProgressiveModal } from '@/components/ui/progressive-modal/progressive-modal.hook';
import { useToast } from '@/components/ui/use-toast';
import { useLoading } from '@/hooks/use-loading.hook';
import { getAnalyticsPlugin } from '@/app/(main)/_components/logger/analytics/get-analytics-plugin';
import { AnalyticsEnum } from '@/types/enums/analytics/analytics.enum';
import { confirmCustomerPhone } from '@/store/auth.store';
import { clearPendingPatient, patientSelectionStore } from '@/store/patient-selection/patient-selection.store';
import { STANDALONE_GUIDE_SCHEDULE_TYPE } from '@/types/enums/analytics/search/search-analytics.enum';
import { updateCustomerPhone } from '@/services/customer/update-customer-phone.service';
import { HttpException } from '@/utils/api/api-client';
import { PatientOption } from '@/types/patient-selection.types';
import { cellphoneValidator } from '@/utils/validators/cellphone.validator';
import { getPhoneMask, isBrazilianPhone, maskPhone, normalizePhoneDigits } from '@/utils/formatters/phonenumber';
import { getLogger } from '@/logger/logger';

const logger = getLogger('patient-selection-phone-confirmation');

const REQUIRED_MESSAGE = 'Telefone é obrigatório';
const INVALID_MESSAGE = 'Telefone inválido';
const UPDATE_ERROR_MESSAGE = 'Erro ao atualizar telefone.';
const UPDATE_SUCCESS_MESSAGE = 'O telefone foi atualizado.';

const PHONE_ERROR_ID = 'patient-selection-phone-error';

const onlyDigits = (value: string): string => value.replace(/\D/g, '');

function buildInitialPhone(currentPhone: string): string {
  return isBrazilianPhone(currentPhone) ? maskPhone(normalizePhoneDigits(currentPhone)) : '';
}

export interface PatientSelectionPhoneConfirmationProps {
  patient: PatientOption | null;
  currentPhone: string;
  onConfirmed: (patient: PatientOption) => void;
}

export function PatientSelectionPhoneConfirmation({
  patient,
  currentPhone,
  onConfirmed,
}: PatientSelectionPhoneConfirmationProps) {
  const { handleJump } = useProgressiveModal<PatientSelectionModalKeys>();
  const { toast } = useToast();
  const { startLoading, stopLoading, isLoading } = useLoading();

  const [phone, setPhone] = useState(() => buildInitialPhone(currentPhone));
  const [isTouched, setIsTouched] = useState(() => !isBrazilianPhone(currentPhone));

  const isValid = cellphoneValidator(phone) && isBrazilianPhone(phone);

  const validationMessage = onlyDigits(phone).length === 0 ? REQUIRED_MESSAGE : INVALID_MESSAGE;
  const errorMessage = isTouched && !isValid ? validationMessage : '';

  const backToList = () => {
    clearPendingPatient();
    handleJump({ key: PatientSelectionModalKeys.LIST, returning: true });
  };

  const handleConfirm = async () => {
    const chosenPatient = patient;

    if (!chosenPatient || !isValid || isLoading) {
      return;
    }

    const digits = normalizePhoneDigits(phone);
    const hasChanged = digits !== normalizePhoneDigits(currentPhone);

    startLoading();

    try {
      await updateCustomerPhone(digits);
    } catch (error) {
      toast.error(UPDATE_ERROR_MESSAGE);

      logger.error('Failed to update the customer phone', {
        error,
        phone_confirmation: {
          event: 'update_failed',
          paciente_tipo: chosenPatient.type,
          response_status: error instanceof HttpException ? error.status : undefined,
        },
      });

      return;
    } finally {
      stopLoading();
    }

    confirmCustomerPhone(digits);

    getAnalyticsPlugin().track(
      hasChanged ? AnalyticsEnum.UPDATE_PHONE : AnalyticsEnum.CONFIRM_PHONE,
      { paciente_tipo: chosenPatient.type },
    );

    if (patientSelectionStore.getState().request?.scheduleType === STANDALONE_GUIDE_SCHEDULE_TYPE) {
      getAnalyticsPlugin().track(AnalyticsEnum.STANDALONE_CONFIRM_CONTACT, {
        paciente_tipo: chosenPatient.type,
      });
    }

    if (hasChanged) {
      toast.success(UPDATE_SUCCESS_MESSAGE);
    }

    onConfirmed(chosenPatient);
  };

  return (
    <div className="flex flex-col gap-4 p-5" data-testid={DataTestIdEnum.PATIENT_SELECTION_PHONE_STEP}>
      <div className="flex items-start gap-2">
        <button
          type="button"
          aria-label="Voltar"
          disabled={isLoading}
          data-testid={DataTestIdEnum.PATIENT_SELECTION_PHONE_STEP_BACK_BUTTON}
          className="shrink-0 flex size-8 items-center justify-center cursor-pointer disabled:cursor-not-allowed disabled:opacity-50"
          onClick={backToList}
        >
          <Icon icon={faArrowLeft} className="text-gray-500 hover:text-gray-900 text-2xl" />
        </button>
        <Typography
          variant={TypographyVariant.MODAL_TITLE}
          data-testid={DataTestIdEnum.PATIENT_SELECTION_PHONE_STEP_TITLE}
        >
          Confirme o telefone
        </Typography>
      </div>

      <Typography
        variant={TypographyVariant.BODY_TEXT}
        className="text-gray-700"
        data-testid={DataTestIdEnum.PATIENT_SELECTION_PHONE_STEP_MESSAGE}
      >
        É importante manter o número do paciente atualizado, pois o profissional fará contato por ele,
        especialmente em teleconsultas.
      </Typography>

      <div className="flex flex-col rounded-lg border border-gray-300 bg-gray-50 p-4">
        <Typography variant={TypographyVariant.MEDIUM_BODY_TEXT} className="text-gray-700">
          Confirme o contato de
        </Typography>
        <Typography
          variant={TypographyVariant.BODY_TEXT}
          className="font-bold text-gray-700"
          data-testid={DataTestIdEnum.PATIENT_SELECTION_PHONE_STEP_PATIENT_NAME}
        >
          {patient?.name}
        </Typography>
      </div>

      <label className="flex flex-col gap-1">
        <Typography as="span" variant={TypographyVariant.MEDIUM_BODY_TEXT} className="font-semibold">
          Telefone
        </Typography>
        <Input
          type="tel"
          inputMode="numeric"
          placeholder="(41) 99999-9999"
          mask={getPhoneMask(phone)}
          value={phone}
          hasError={Boolean(errorMessage)}
          aria-invalid={Boolean(errorMessage)}
          aria-describedby={errorMessage ? PHONE_ERROR_ID : undefined}
          data-testid={DataTestIdEnum.PATIENT_SELECTION_PHONE_INPUT}
          onChange={(event) => setPhone(event.target.value)}
          onBlur={() => setIsTouched(true)}
          onClear={() => setIsTouched(true)}
        />
      </label>

      {errorMessage && (
        <p
          id={PHONE_ERROR_ID}
          className="text-sm font-medium text-danger-600"
          data-testid={DataTestIdEnum.PATIENT_SELECTION_PHONE_ERROR}
        >
          {errorMessage}
        </p>
      )}

      <Button
        disabled={!isValid}
        isLoading={isLoading}
        data-testid={DataTestIdEnum.PATIENT_SELECTION_PHONE_CONFIRM_BUTTON}
        onClick={handleConfirm}
      >
        Ok, continuar
      </Button>
    </div>
  );
}
